import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('swiftdash_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export default api;

// Auth
export const authApi = {
  signup: (data) => api.post('/auth/signup', data).then((r) => r.data),
  login: (data) => api.post('/auth/login', data).then((r) => r.data),
  google: (sessionId) => api.post('/auth/google', null, { headers: { 'X-Session-ID': sessionId } }).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  logout: () => api.post('/auth/logout').then((r) => r.data),
};

// Catalog
export const catalogApi = {
  categories: () => api.get('/categories').then((r) => r.data),
  promos: () => api.get('/promos').then((r) => r.data),
  vendors: (params) => api.get('/vendors', { params }).then((r) => r.data),
  vendor: (id) => api.get(`/vendors/${id}`).then((r) => r.data),
};

// Orders
export const ordersApi = {
  create: (body) => api.post('/orders', body).then((r) => r.data),
  list: () => api.get('/orders').then((r) => r.data),
  get: (id) => api.get(`/orders/${id}`).then((r) => r.data),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }).then((r) => r.data),
  activeSummary: (limit = 5) => api.get('/me/active-orders-summary', { params: { limit } }).then((r) => r.data),
  // Public shareable delivery receipt
  createReceiptShare: (id) => api.post(`/orders/${id}/receipt/share`).then((r) => r.data),
  publicReceipt: (id, t) => api.get(`/orders/${id}/receipt/public`, { params: { t } }).then((r) => r.data),
  driverLocation: (id) => api.get(`/orders/${id}/driver-location`).then((r) => r.data),
  // Shipper: for customer-as-vendor logistics/same-day/courier orders.
  shipperUpdateStatus: (id, status) => api.patch(`/me/orders/${id}/status`, { status }).then((r) => r.data),
};

// Customer profile: saved addresses.
export const addressesApi = {
  list: () => api.get('/me/addresses').then((r) => r.data),
  add: (payload) => api.post('/me/addresses', payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/me/addresses/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/me/addresses/${id}`).then((r) => r.data),
};

// Two-way ratings (customer ↔ driver, customer ↔ store).
export const ratingsApi = {
  create: (payload) => api.post('/ratings', payload).then((r) => r.data),
  forOrder: (orderId) => api.get(`/ratings/order/${orderId}`).then((r) => r.data),
};

// Shipper (customer-as-vendor for courier / logistics / same-day orders).
// Kept as a thin alias so any imports elsewhere still resolve.
export const shipperApi = {
  updateStatus: (id, status) => api.patch(`/me/orders/${id}/status`, { status }).then((r) => r.data),
};

// Payments (Stripe)
export const paymentsApi = {
  checkout: (order_id) => api.post('/payments/checkout', { order_id, origin_url: window.location.origin }).then((r) => r.data),
  status: (session_id) => api.get(`/payments/status/${session_id}`).then((r) => r.data),
};

// Admin
export const adminApi = {
  stats: () => api.get('/admin/stats').then((r) => r.data),
  revenue: () => api.get('/admin/revenue').then((r) => r.data),
  // Users
  users: (params) => api.get('/admin/users', { params }).then((r) => r.data),
  updateUser: (id, body) => api.patch(`/admin/users/${id}`, body).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  bulkUsers: (user_ids, action) => api.post('/admin/users/bulk', { user_ids, action }).then((r) => r.data),
  bulkDrivers: (driver_ids, action) => api.post('/admin/drivers/bulk', { driver_ids, action }).then((r) => r.data),
  bulkDriverZones: (driver_ids, zone_ids, mode) => api.post('/admin/drivers/bulk-zones', { driver_ids, zone_ids, mode }).then((r) => r.data),
  courierApplications: (status) => api.get('/admin/courier-applications', { params: status ? { status } : undefined }).then((r) => r.data),
  getStatusMessages: () => api.get('/admin/status-messages').then((r) => r.data),
  saveStatusMessages: (messages) => api.put('/admin/status-messages', { messages }).then((r) => r.data),
  listAnnouncements: () => api.get('/admin/announcements').then((r) => r.data),
  createAnnouncement: (body) => api.post('/admin/announcements', typeof body === 'string' ? { text: body } : body).then((r) => r.data),
  updateAnnouncement: (id, body) => api.put(`/admin/announcements/${id}`, body).then((r) => r.data),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`).then((r) => r.data),
  approveCourierApplication: (id) => api.post(`/admin/courier-applications/${id}/approve`).then((r) => r.data),
  rejectCourierApplication: (id, reason) => api.post(`/admin/courier-applications/${id}/reject`, { reason }).then((r) => r.data),
  heatmap: (city) => api.get('/admin/heatmap/coverage', { params: { city } }).then((r) => r.data),
  // Vendors
  vendors: () => api.get('/admin/vendors').then((r) => r.data),
  createVendor: (body) => api.post('/admin/vendors', body).then((r) => r.data),
  updateVendor: (id, body) => api.patch(`/admin/vendors/${id}`, body).then((r) => r.data),
  deleteVendor: (id) => api.delete(`/admin/vendors/${id}`).then((r) => r.data),
  // Menus
  getMenu: (vendorId) => api.get(`/admin/vendors/${vendorId}/menu`).then((r) => r.data),
  putMenu: (vendorId, sections) => api.put(`/admin/vendors/${vendorId}/menu`, { sections }).then((r) => r.data),
  // Orders
  orders: (params) => api.get('/admin/orders', { params }).then((r) => r.data),
  updateOrderStatus: (id, status) => api.patch(`/admin/orders/${id}/status`, { status }).then((r) => r.data),
  confirmPlatformPayment: (id, method, reference) => api.post(
    `/admin/orders/${id}/confirm-platform-payment`,
    { method, reference },
  ).then((r) => r.data),
  // E-transfer verifier
  listETransferSubmissions: (status = 'pending') => api.get('/admin/etransfer/submissions', { params: { status } }).then((r) => r.data),
  approveETransfer: (id, note) => api.post(`/admin/etransfer/submissions/${id}/approve`, { note }).then((r) => r.data),
  rejectETransfer: (id, note) => api.post(`/admin/etransfer/submissions/${id}/reject`, { note }).then((r) => r.data),
  getOrderETransfer: (orderId, invoice_kind) => api.get(`/admin/etransfer/submissions/order/${orderId}`, { params: invoice_kind ? { invoice_kind } : undefined }).then((r) => r.data),
  deleteOrder: (id) => api.delete(`/admin/orders/${id}`).then((r) => r.data),
  // Promos
  createPromo: (body) => api.post('/admin/promos', body).then((r) => r.data),
  updatePromo: (id, body) => api.patch(`/admin/promos/${id}`, body).then((r) => r.data),
  deletePromo: (id) => api.delete(`/admin/promos/${id}`).then((r) => r.data),
  // Categories
  createCategory: (body) => api.post('/admin/categories', body).then((r) => r.data),
  updateCategory: (id, body) => api.patch(`/admin/categories/${id}`, body).then((r) => r.data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`).then((r) => r.data),
};

// Vendor admin
export const vendorAdminApi = {
  stats: (vendor_id) => api.get('/vendor-admin/stats', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  store: (vendor_id) => api.get('/vendor-admin/store', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  updateStore: (body, vendor_id) => api.patch('/vendor-admin/store', body, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  menu: (vendor_id) => api.get('/vendor-admin/menu', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  putMenu: (sections, vendor_id) => api.put('/vendor-admin/menu', { sections }, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  orders: (params, vendor_id) => api.get('/vendor-admin/orders', { params: { ...(params || {}), ...(vendor_id ? { vendor_id } : {}) } }).then((r) => r.data),
  updateOrderStatus: (id, status, vendor_id) => api.patch(`/vendor-admin/orders/${id}/status`, { status }, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  pickOrderItem: (id, payload, vendor_id) => api.post(`/vendor-admin/orders/${id}/pick`, payload, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  deleteOrder: (id, vendor_id) => api.delete(`/vendor-admin/orders/${id}`, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  confirmPickup: (id, token, vendor_id) => api.post(`/vendor-admin/orders/${id}/confirm-pickup`, { token }, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  confirmGoodsPayment: (id, method, reference, vendor_id) => api.post(
    `/vendor-admin/orders/${id}/confirm-goods-payment`,
    { method, reference },
    { params: vendor_id ? { vendor_id } : undefined },
  ).then((r) => r.data),
  // Vendor gift-card pool
  getGiftPool: (vendor_id) => api.get('/vendor-admin/gift-pool', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  createGiftPoolTopupSession: (amount_cad, origin_url, vendor_id) => api.post(
    '/vendor-admin/gift-pool/topup/session',
    { amount_cad, origin_url },
    { params: vendor_id ? { vendor_id } : undefined },
  ).then((r) => r.data),
  finalizeGiftPoolTopup: (session_id, vendor_id) => api.get(
    `/vendor-admin/gift-pool/topup/session/${session_id}`,
    { params: vendor_id ? { vendor_id } : undefined },
  ).then((r) => r.data),
  getOrderETransfer: (orderId, vendor_id) => api.get(
    `/vendor-admin/orders/${orderId}/etransfer`,
    { params: vendor_id ? { vendor_id } : undefined },
  ).then((r) => r.data),
  preferredDriverStatus: (vendor_id) => api.get('/vendor-admin/preferred-driver/status', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  getDigestPreference: (vendor_id) => api.get('/vendor-admin/digest-preference', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  setDigestPreference: (frequency, vendor_id) => api.patch('/vendor-admin/digest-preference', { frequency }, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  getPaystackSubaccount: (vendor_id) => api.get('/vendor-admin/paystack/subaccount', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  savePaystackSubaccount: (body, vendor_id) => api.post('/vendor-admin/paystack/subaccount', body, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  paystackBanks: (country) => api.get('/paystack/banks', { params: { country } }).then((r) => r.data),
  store: (vendor_id) => api.get('/vendor-admin/store', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  updateStore: (updates, vendor_id) => api.patch('/vendor-admin/store', updates, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
};

// Multi-store: user-facing endpoints for listing / creating their vendor stores
export const myVendorStoresApi = {
  list: () => api.get('/me/vendor-stores').then((r) => r.data),
  create: (body) => api.post('/me/vendor-stores', body).then((r) => r.data),
};

// Admin — multi-email owners per vendor
export const adminOwnersApi = {
  list: (vendor_id) => api.get(`/admin/vendors/${vendor_id}/owners`).then((r) => r.data),
  add: (vendor_id, email) => api.post(`/admin/vendors/${vendor_id}/owners`, { email }).then((r) => r.data),
  remove: (vendor_id, user_id) => api.delete(`/admin/vendors/${vendor_id}/owners/${user_id}`).then((r) => r.data),
};

// Admin — vendor pending-location approvals (2nd-edit gate)
export const adminLocationApprovalApi = {
  listPending: () => api.get('/admin/vendors/pending-location').then((r) => r.data),
  approve: (vendor_id) => api.patch(`/admin/vendors/${vendor_id}/location-approval`, { action: 'approve' }).then((r) => r.data),
  reject: (vendor_id, reason) => api.patch(`/admin/vendors/${vendor_id}/location-approval`, { action: 'reject', reason }).then((r) => r.data),
};

// Driver
export const driverApi = {
  apply: (body) => api.post('/driver/apply', body).then((r) => r.data),
  me: () => api.get('/driver/me').then((r) => r.data),
  updateMe: (body) => api.patch('/driver/me', body).then((r) => r.data),
  goOnline: (lat, lng) => api.post('/driver/online', { lat, lng }).then((r) => r.data),
  goOffline: () => api.post('/driver/offline').then((r) => r.data),
  updateLocation: (lat, lng) => api.post('/driver/location', { lat, lng }).then((r) => r.data),
  stats: () => api.get('/driver/stats').then((r) => r.data),
  orders: (active_only) => api.get('/driver/orders', { params: active_only ? { active_only: true } : undefined }).then((r) => r.data),
  acceptOrder: (id) => api.patch(`/driver/orders/${id}/accept`).then((r) => r.data),
  declineOrder: (id) => api.patch(`/driver/orders/${id}/decline`).then((r) => r.data),
  updateOrderStatus: (id, status, extra = null) => api.patch(`/driver/orders/${id}/status`, { status, ...(extra || {}) }).then((r) => r.data),
  verifyQr: (id, token) => api.post(`/driver/orders/${id}/verify-qr`, { token }).then((r) => r.data),
  scanQrAny: (token) => api.post(`/driver/qr/scan`, { token }).then((r) => r.data),
  orderQr: (id) => api.get(`/orders/${id}/qr`).then((r) => r.data),
  uploadPod: (id, file_ids) => api.post(`/driver/orders/${id}/pod`, { file_ids }).then((r) => r.data),
  availableOrders: () => api.get('/driver/orders/available').then((r) => r.data),
  claimAvailable: (id) => api.post(`/driver/orders/${id}/claim`).then((r) => r.data),
  releaseOrder: (id) => api.post(`/driver/orders/${id}/release`).then((r) => r.data),
  route: () => api.get('/driver/route').then((r) => r.data),
  optimizeRoute: () => api.get('/driver/route/optimize').then((r) => r.data),
  weeklyEarnings: (week_of) => api.get('/driver/earnings/weekly', { params: week_of ? { week_of } : undefined }).then((r) => r.data),
  courierApplication: () => api.get('/driver/courier-application').then((r) => r.data),
  submitCourierApplication: (body) => api.post('/driver/courier-application', body).then((r) => r.data),
  weeklyEarningsCsv: async (week_of) => {
    const r = await api.get('/driver/earnings/weekly.csv', {
      params: week_of ? { week_of } : undefined,
      responseType: 'blob',
    });
    return r.data;
  },
};

// Vendor team management
export const vendorTeamApi = {
  list: (vendor_id) => api.get('/vendor-admin/team', { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  invite: (body, vendor_id) => api.post('/vendor-admin/team', body, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  updateRole: (email, role, vendor_id) => api.patch(`/vendor-admin/team/${encodeURIComponent(email)}`, { role }, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
  remove: (email, vendor_id) => api.delete(`/vendor-admin/team/${encodeURIComponent(email)}`, { params: vendor_id ? { vendor_id } : undefined }).then((r) => r.data),
};

export const orderQrApi = {
  get: (orderId) => api.get(`/orders/${orderId}/qr`).then((r) => r.data),
};

// Admin drivers
export const adminDriverApi = {
  list: (status_f) => api.get('/admin/drivers', { params: status_f ? { status_f } : undefined }).then((r) => r.data),
  update: (id, body) => api.patch(`/admin/drivers/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/drivers/${id}`).then((r) => r.data),
  reassignOrder: (orderId, body) => api.post(`/admin/orders/${orderId}/reassign`, body || {}).then((r) => r.data),
};
